import React, { useEffect, useRef } from 'react'
import { useDebounce, useDebounceEffect, useSetState } from 'ahooks'
import { Button, message, Select, Spin, Upload } from 'antd'
import {
  SActionBtn,
  SDrawerOrModal,
  SFuncModalFormRef,
  SLowcodeList,
  SMessage,
  STableActions,
} from 'shj-components'
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons'

import { httpOrgList } from '@/services/homeService.ts'
import {
  getBrandList,
  getBrandPage,
  getProductCommonPage,
  getProductManagementPage,
  getSpecialCodesList,
} from '@/services/product.ts'
import { apiData2Table } from '@/utils/data.ts'
import { getConfig } from './config.tsx'
import AddOrEditConfigure from './addOrEdit'
import { httpDictServList } from '@/services'
import DebounceSelect from '@/pages/baseProduct/components/DebounceSearchSelect'
import DebounceSearchSelect from '@/pages/baseProduct/components/DebounceSearchSelect'
import { fileDownload } from '@/utils/download.ts'

const PriceStrategyTable = props => {
  const { current, apiUrlObj } = props
  const listRef = useRef()
  const editRef = useRef<SFuncModalFormRef>({ onFinish: undefined })
  const [state, setState] = useSetState<any>({
    exportLoading: false,
    importLoading: false,
    visible: false,
    loading: false,
    record: {},
  })

  const fetchList = ({ current, pageSize, query }) => {
    return new Promise((resolve, reject) => {
      apiUrlObj
        .getPage({
          params: { pageId: current, pageSize },
          data: {
            ...query,
            [apiUrlObj.searchKey]: props[apiUrlObj.searchKey],
            queryCompanyName: true,
            queryProductName: true,
            queryPriceType: true,
            querySource: true,
            queryBrandName: true,
          },
        })
        .then(res => {
          resolve(apiData2Table(res))
        })
        .catch(reject)
    })
  }

  const onAddOrEdit = (record = {}) => {
    setState({ visible: true, record: { ...record } })
  }

  const onDel = (id: string) => {
    SMessage.confirm({
      onOk: () => {
        return apiUrlObj.deleteById(id).then((res: any) => {
          message.success(res.message || '操作成功')
          listRef.current?.reload()
        })
      },
    })
  }

  const onBatchDelete = ({ selectedRowKeys }) => {
    SMessage.confirm({
      content: `是否确认删除所选数据`,
      onOk: () => {
        return apiUrlObj.batchDelete({ ids: selectedRowKeys }).then((res: any) => {
          message.success(res.message || '操作成功')
          listRef.current?.reload()
        })
      },
    })
  }

  const onSubmit = () => {
    editRef.current.onFinish?.()
  }

  const onCancel = () => {
    setState({ visible: false })
  }

  const onImport = (upload: any) => {
    setState({ importLoading: true })
    const { file, onSuccess, onError } = upload
    const data = new FormData()
    data.append('file', file)
    return apiUrlObj
      .importFile(data, current.key)
      .then((res: any) => {
        listRef.current && listRef.current.reload()
        onSuccess && onSuccess()
        message.success(res.message || '导入成功')
      })
      .catch(onError)
      .finally(() => setState({ importLoading: false }))
  }

  const onExport = () => {
    setState({ exportLoading: true })
    fileDownload(apiUrlObj.download + `${apiUrlObj.searchKey ? current.key : ''}`)
      .then(() => setState({ exportLoading: false }))
      .catch(() => setState({ exportLoading: false }))
  }

  const getStatusOptions = async () => {
    const companyList: any = await httpOrgList()
    const priceTypeList = await httpDictServList({ parentKey: 'MpmsPriceType' })
    const brandList: any = await getBrandList()
       let specialCodeList: any = await getSpecialCodesList({
          data: {},
        })
    return Promise.resolve({
      companyList: [{ name: '通用', id: '0' }, ...companyList],
      priceTypeList,
      brandList: [{ name: '通用', id: '0' }, ...brandList],
      specialCodeList,
    })
  }

  const extensions = {
    request: { fetchList, getStatusOptions },
    events: { onAdd: () => onAddOrEdit(), onBatchDelete },
    customRender: {
      jsonRender(v: any) {
        const list = v && JSON.parse(v)?.shops
        return list?.map(item => item.name)?.join(', ')
      },
      actionRender(_: null, record: Record<string, any>) {
        return (
          <STableActions>
            <SActionBtn onClick={() => onAddOrEdit(record)}>编辑</SActionBtn>
            <SActionBtn danger onClick={() => onDel(record.id)}>
              删除
            </SActionBtn>
          </STableActions>
        )
      },
      importRender: () => {
        return (
          <Upload listType="text" customRequest={onImport} itemRender={() => null}>
            <Button
              type={'primary'}
              loading={state.importLoading}
              disabled={state.importLoading}
              icon={<UploadOutlined />}
            >
              批量导入
            </Button>
          </Upload>
        )
      },
      exportRender: () => {
        return (
          <Button
            icon={<DownloadOutlined />}
            onClick={onExport}
            disabled={state.exportLoading}
            loading={state.exportLoading}
          >
            下载导入模板
          </Button>
        )
      },
      searchSelectRender({ name, form }) {
        return (
          <DebounceSearchSelect
            allowClear
            label={'产品'}
            isCommon={true}
            fixedParams={{enabled: undefined, prodType: "PRODUCT_PACKAGE"}}
            fetchOptions={getProductCommonPage}
            onChange={v => form.setFieldsValue({ [name]: v })}
            fieldNames={{ label: 'prodName' }}
            style={{ width: '100%' }}
          />
        )
      },
      // searchBrandSelectRender({ name, form }) {
      //   return (
      //     <DebounceSelect
      //       allowClear
      //       label={'品牌'}
      //       fetchOptions={getBrandList}
      //       onChange={v => form.setFieldsValue({ [name]: v })}
      //       style={{ width: '100%' }}

      //     />
      //   )
      // },
    },
  }

  return (
    <div>
      <SLowcodeList
        ref={listRef}
        searchMode={'group'}
        config={getConfig(current)}
        {...extensions}
      />
      <SDrawerOrModal
        mode={'modal'}
        width={500}
        onCancel={onCancel}
        onOk={onSubmit}
        open={state.visible}
        loading={state.loading}
        title={(state.record.id ? '编辑' : '新增') + '配置'}
      >
        <AddOrEditConfigure
          fn={listRef.current?.reload}
          onClose={onCancel}
          apiObj={apiUrlObj}
          editRef={editRef}
          current={current}
          setLoading={(loading: boolean) => setState({ loading })}
          record={state.record}
        />
      </SDrawerOrModal>
    </div>
  )
}

export default PriceStrategyTable
